/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { api } from "../../../lib/auth";
import { toast } from "sonner";
import { FileText, Plus, Loader2, Calendar, TrendingUp, AlertCircle } from "lucide-react";

const ngn = (n) => `₦${Number(n || 0).toLocaleString()}`;

export function DailyReportsView({ submitOnly = false }) {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(submitOnly);
  const [agg, setAgg] = useState(null);

  const load = async () => {
    try { setItems((await api.get("/t/re/daily-reports")).data || []); } catch { setItems([]); }
    try { setAgg((await api.get("/t/re/daily-reports/aggregate?period=weekly")).data); } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="re-reports-view">
      {agg && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <KPI title="Reports (7d)" value={agg.kpis.reports} />
          <KPI title="Customers contacted" value={agg.kpis.customers_contacted} />
          <KPI title="Inspections" value={agg.kpis.inspections_done} />
          <KPI title="Leads generated" value={agg.kpis.leads_generated} />
          <KPI title="Revenue" value={ngn(agg.kpis.revenue_generated)} />
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="font-display font-extrabold text-base">Daily reports</div>
        {submitOnly && (
          <button onClick={() => setShow(true)} data-testid="re-report-submit-btn"
            className="px-4 py-2.5 rounded-lg bg-[#1FA868] text-white text-sm font-bold flex items-center gap-2"><Plus size={14} /> Submit today's report</button>
        )}
      </div>

      <div className="bg-white border border-black/8 rounded-2xl overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center"><FileText size={28} className="mx-auto text-black/25 mb-2" /><div className="font-bold text-black/55">No reports yet</div></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide font-bold text-black/55">
              <tr>
                <th className="px-4 py-2.5 text-left">Date</th>
                <th className="px-4 py-2.5 text-left">Marketer</th>
                <th className="px-4 py-2.5 text-right">Contacted</th>
                <th className="px-4 py-2.5 text-right">Inspections</th>
                <th className="px-4 py-2.5 text-right">Leads</th>
                <th className="px-4 py-2.5 text-right">Deals</th>
                <th className="px-4 py-2.5 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-black/5" data-testid={`re-report-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{r.report_date}</td>
                  <td className="px-4 py-3 font-bold">{r.marketer_name}</td>
                  <td className="px-4 py-3 text-right">{r.customers_contacted}</td>
                  <td className="px-4 py-3 text-right">{r.inspections_done}</td>
                  <td className="px-4 py-3 text-right">{r.leads_generated}</td>
                  <td className="px-4 py-3 text-right font-bold text-blue-700">{r.closed_deals}</td>
                  <td className="px-4 py-3 text-right font-bold text-emerald-700">{ngn(r.revenue_generated)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {show && <ReportSubmitForm onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}

function KPI({ title, value }) {
  return (
    <div className="bg-white border border-black/8 rounded-xl p-3.5">
      <div className="text-[10px] uppercase font-bold text-black/55">{title}</div>
      <div className="font-display font-extrabold text-xl text-black mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function ReportSubmitForm({ onClose, onSaved }) {
  const [f, setF] = useState({
    customers_contacted: 0, inspections_done: 0, leads_generated: 0, closed_deals: 0,
    revenue_generated: 0, activities_performed: "", challenges_faced: "", planned_activities: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/t/re/daily-reports", f);
      toast.success("Report submitted");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="re-report-form">
        <div className="p-6 border-b border-black/8 flex items-center gap-3">
          <Calendar size={18} className="text-blue-600" />
          <div className="font-display font-extrabold text-lg">Submit daily marketing report</div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Num lbl="Customers contacted" v={f.customers_contacted} onChange={(v) => setF({ ...f, customers_contacted: v })} testid="rep-contacted" />
            <Num lbl="Inspections done" v={f.inspections_done} onChange={(v) => setF({ ...f, inspections_done: v })} testid="rep-inspections" />
            <Num lbl="Leads generated" v={f.leads_generated} onChange={(v) => setF({ ...f, leads_generated: v })} testid="rep-leads" />
            <Num lbl="Closed deals" v={f.closed_deals} onChange={(v) => setF({ ...f, closed_deals: v })} testid="rep-deals" />
          </div>
          <Num lbl="Revenue generated (₦)" v={f.revenue_generated} onChange={(v) => setF({ ...f, revenue_generated: v })} testid="rep-revenue" />
          <Area lbl="Activities performed" v={f.activities_performed} onChange={(v) => setF({ ...f, activities_performed: v })} testid="rep-activities" />
          <Area lbl="Challenges faced" v={f.challenges_faced} onChange={(v) => setF({ ...f, challenges_faced: v })} testid="rep-challenges" />
          <Area lbl="Planned activities for next day" v={f.planned_activities} onChange={(v) => setF({ ...f, planned_activities: v })} testid="rep-planned" />
          <Area lbl="Additional notes" v={f.notes} onChange={(v) => setF({ ...f, notes: v })} />
        </div>
        <div className="p-5 border-t border-black/8 flex gap-2">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-lg bg-black/5 font-bold text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="rep-save"
            className="flex-1 py-2.5 rounded-lg bg-[#1FA868] text-white font-bold text-sm disabled:opacity-50">{saving ? "Submitting…" : "Submit report"}</button>
        </div>
      </div>
    </div>
  );
}

function Num({ lbl, v, onChange, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase font-bold text-black/55">{lbl}</label>
      <input type="number" value={v} onChange={(e) => onChange(parseFloat(e.target.value) || 0)} data-testid={testid}
        className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2 text-sm tabular-nums" />
    </div>
  );
}
function Area({ lbl, v, onChange, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase font-bold text-black/55">{lbl}</label>
      <textarea value={v} onChange={(e) => onChange(e.target.value)} rows={2} data-testid={testid}
        className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2 text-sm" />
    </div>
  );
}
